/**
 * Addresses: `/login`, `/decide`, `/<view>`, `/<view>/<section-slug>`, `/<view>/<section>/<screen>`.
 * History API only — no router library; the shell's `onGo` lands here.
 */
import { useEffect, useState } from 'react';
import type { ViewId } from '@cider/ui';
import { VIEW_TAGS } from '@cider/ui';
import { moneyMove } from '../views/money/moves.js';

export const slug = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[’']/g, '')
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export interface Route {
  path: string;
  view: ViewId | null;
  /**
   * `app` is the chair app — its own surface at `/app`, not a screen of the director's shell.
   * A chair opens a phone, not a rail: no switcher, no six views, seven tabs across the bottom
   * (`Chairs.dc.html`, drawn at 390 × 844). A director reaches the same surface through
   * "View as a chair", full-bleed, with the read-only banner the drawing puts above it.
   */
  page:
    | 'login'
    | 'decide'
    | 'view'
    | 'app'
    | 'print'
    | 'whats-new'
    | 'account'
    | 'answer'
    | 'booth'
    | 'wall'
    | 'booth-guest'
    | 'form'
    | 'tv'
    | 'unknown';
  section: string | null;
  screen: string | null;
  /** For /login/link/:token and /login/invite/:token. */
  token: string | null;
  lane: 'link' | 'invite' | null;
}

export function parse(path: string): Route {
  const parts = path.split('/').filter(Boolean);
  const base: Route = {
    path,
    view: null,
    page: 'unknown',
    section: null,
    screen: null,
    token: null,
    lane: null,
  };
  const [a, b, c] = parts;
  if (!a) return { ...base, page: 'decide' };
  if (a === 'login') {
    if ((b === 'link' || b === 'invite') && c) return { ...base, page: 'login', lane: b, token: c };
    return { ...base, page: 'login' };
  }
  if (a === 'decide') return { ...base, page: 'decide' };
  // /answer/:token/:choice — the invitation's question (Oct 2); no sign-in, the link is the key.
  if (a === 'answer' && b) return { ...base, page: 'answer', token: b, section: c ?? null };
  // /app · /app/<tab> · /app/<tab>/<screen> — the chair app's own address.
  // A check-in station's door: `/booth`, or the station's own address `/booth/<token>`.
  // The guest screen beside a station (the two screens): /booth/guest, never a station token.
  // ADR 0034: a small form's own page, /f/<token> — the link is the key; nobody need sign in.
  if (a === 'f' && b) return { ...base, page: 'form', token: b };
  if (a === 'booth' && b === 'guest') return { ...base, page: 'booth-guest' };
  if (a === 'booth') return { ...base, page: 'booth', token: b ?? null };
  if (a === 'app') return { ...base, page: 'app', section: b ?? null, screen: c ?? null };
  // Watchtower's wall: one screen for the TV, outside the shell (the ninth overnight).
  // Stream B (James, Oct 18): /wall/money · /wall/count-room · /wall/directors, each its own TV.
  if (a === 'wall') return { ...base, page: 'wall', section: b ?? null };
  // K2a: a signage screen's frame, /tv/<key> — the key is the screen's own; nobody signs in.
  if (a === 'tv' && b) return { ...base, page: 'tv', token: b };
  if (a === 'whats-new') return { ...base, page: 'whats-new' };
  if (a === 'account') return { ...base, page: 'account' };
  if (a === 'print') return { ...base, page: 'print', section: b ?? null, screen: c ?? null };
  if (a in VIEW_TAGS)
    return { ...base, page: 'view', view: a as ViewId, section: b ?? null, screen: c ?? null };
  return base;
}

export function go(href: string): void {
  if (window.location.pathname + window.location.search === href) return;
  window.history.pushState(null, '', href);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/**
 * The old spellings still open the screen (the Sep 20 ruling): the address is rewritten in place.
 *
 * **A renamed screen keeps its old address** (James, Oct 1 — A8). An address here is slugged
 * from the screen's title, so renaming "Audit" to "Who did what" — its name in the code, in its
 * tests and on the canvas — moved the screen's URL with it. The canvas still points at the old
 * one and so does anything anybody wrote down, so the old one answers and lands on the new.
 */
const OLD_PATHS: readonly [RegExp, string][] = [
  [/\/organisations(?=[/?]|$)/, '/organizations'], // spelling: the old path
  [/\/money\/close-and-reports\/audit(?=[/?]|$)/, '/money/close-and-reports/who-did-what'],
  // The Chair record was folded into the person record (batch 1b): one person, one record, one
  // address. The old address carries its `?person=` straight across.
  [/\/admin\/people-and-roles\/chair-record(?=[/?]|$)/, '/admin/people-and-roles/people'],
  // A bare /status is the system's status screen (James, Oct 18); the API's own is /api/status.
  [/^\/status(?=[/?]|$)/, '/admin/system/status'],
];
function current(): string {
  let href = window.location.pathname + window.location.search;
  for (const [old, now] of OLD_PATHS) {
    if (!old.test(href)) continue;
    href = href.replace(old, now).replace('?organisation=', '?organization='); // spelling: the old key
    window.history.replaceState(null, '', href);
  }
  // The rail fold (James, Oct 15): every old Money address lands on its new home.
  const moved = moneyMove(href);
  if (moved) {
    href = moved;
    window.history.replaceState(null, '', href);
  }
  return href;
}

export function useRoute(): Route {
  // The query string is part of where we are (`?person=…` opens a record on the same screen), so a
  // change to it alone re-renders too; the route itself is parsed from the path.
  const [href, setHref] = useState(current);
  useEffect(() => {
    const onPop = (): void => {
      setHref(current());
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
    };
  }, []);
  return parse(href.split('?')[0] ?? href);
}
